import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import axios from 'axios';
import { Map } from 'immutable';
import { bundle } from '../helpers';
var types = {
  400: 'badRequest',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'notFound',
  405: 'methodNotAllowed'
};
export var handleErrors = function handleErrors(error) {
  // handle a javascript runtime exception by re-throwing it, this is in case we
  // make a mistake in a `then` block in one of our api functions.
  if (error instanceof Error && !error.response) {
    throw error;
  } // Destructure out the information needed.


  var _error$response = error.response,
      _error$response$data = _error$response.data,
      data = _error$response$data === void 0 ? {} : _error$response$data,
      statusCode = _error$response.status,
      statusText = _error$response.statusText;
  var type = types[statusCode];

  var errorMessage = data.error,
      _data$errorKey = data.errorKey,
      key = _data$errorKey === void 0 ? null : _data$errorKey,
      message = data.message,
      rest = _objectWithoutProperties(data, ["error", "errorKey", "message"]);

  var result = typeof data === 'string' ? {
    message: data,
    statusCode: statusCode,
    key: key
  } : _objectSpread(_objectSpread({}, rest), {}, {
    message: errorMessage || message || statusText,
    key: key,
    statusCode: statusCode
  });

  if (type) {
    result[type] = true;
  }

  return {
    error: result
  };
};
export var paramBuilder = function paramBuilder(options) {
  var params = {};
  if (options.include) params.include = options.include;
  if (options.limit) params.limit = options.limit;
  if (options.pageToken) params.pageToken = options.pageToken;
  if (options.q) params.q = options.q;
  if (options.direction) params.direction = options.direction;
  if (options.orderBy) params.orderBy = options.orderBy;
  if (options.manage) params.manage = options.manage;
  if (options["export"]) params["export"] = options["export"];
  if (options.days) params.days = options.days;
  if (options.count) params.count = options.count;
  return params;
};
export var headerBuilder = function headerBuilder(options) {
  var headers = {};

  if (!options["public"]) {
    headers['X-Kinetic-AuthAssumed'] = 'true';
  }

  return headers;
};
export var validateOptions = function validateOptions(functionName, requiredOptions, options) {
  var missing = requiredOptions.filter(function (requiredOption) {
    return !options[requiredOption];
  });

  if (missing.length > 0) {
    throw new Error("".concat(functionName, " failed! The following required options are missing: ").concat(missing));
  }
};
export var apiFunction = function apiFunction(_ref) {
  var name = _ref.name,
      method = _ref.method,
      dataOption = _ref.dataOption,
      requiredOptions = _ref.requiredOptions,
      url = _ref.url,
      transform = _ref.transform;
  return function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    validateOptions(name, dataOption ? [].concat(_toConsumableArray(requiredOptions), [dataOption]) : requiredOptions, options);
    return axios({
      method: method,
      url: bundle.apiLocation() + url(options),
      data: dataOption && options[dataOption],
      params: paramBuilder(options),
      headers: headerBuilder(options)
    }).then(transform)["catch"](handleErrors);
  };
};
export var apiGroup = function apiGroup(_ref2) {
  var _ref3;

  var dataOption = _ref2.dataOption,
      name = _ref2.name,
      plural = _ref2.plural,
      singular = _ref2.singular;
  return _ref3 = {}, _defineProperty(_ref3, "fetch".concat(name, "s"), apiFunction(_objectSpread({
    name: "fetch".concat(name, "s"),
    method: 'get'
  }, plural))), _defineProperty(_ref3, "fetch".concat(name), apiFunction(_objectSpread({
    name: "fetch".concat(name),
    method: 'get'
  }, singular))), _defineProperty(_ref3, "create".concat(name), apiFunction(_objectSpread(_objectSpread({
    name: "create".concat(name),
    dataOption: dataOption,
    method: 'post'
  }, plural), {}, {
    transform: singular.transform
  }))), _defineProperty(_ref3, "update".concat(name), apiFunction(_objectSpread({
    name: "update".concat(name),
    dataOption: dataOption,
    method: 'put'
  }, singular))), _defineProperty(_ref3, "delete".concat(name), apiFunction(_objectSpread({
    name: "delete".concat(name),
    method: 'delete'
  }, singular))), _ref3;
};
export var formPath = function formPath(_ref4) {
  var form = _ref4.form,
      kapp = _ref4.kapp,
      datastore = _ref4.datastore;
  return datastore ? form ? "".concat(bundle.spaceLocation(), "/app/datastore/forms/").concat(form) : "".concat(bundle.spaceLocation(), "/app/datastore/forms") : form ? "".concat(bundle.spaceLocation(), "/").concat(kapp || bundle.kappSlug(), "/").concat(form) : "".concat(bundle.spaceLocation(), "/").concat(kapp || bundle.kappSlug());
};
export var submissionPath = function submissionPath(_ref5) {
  var submission = _ref5.submission,
      datastore = _ref5.datastore;
  return datastore ? submission ? "".concat(bundle.spaceLocation(), "/app/datastore/submissions/").concat(submission) : "".concat(bundle.spaceLocation(), "/app/datastore/submissions") : submission ? "".concat(bundle.spaceLocation(), "/submissions/").concat(submission) : "".concat(bundle.spaceLocation(), "/submissions");
};
export var corePath = function corePath(_ref6) {
  var submission = _ref6.submission,
      kapp = _ref6.kapp,
      form = _ref6.form,
      _ref6$datastore = _ref6.datastore,
      datastore = _ref6$datastore === void 0 ? false : _ref6$datastore;
  return submission ? submissionPath({
    submission: submission,
    datastore: datastore
  }) : formPath({
    form: form,
    kapp: kapp,
    datastore: datastore
  });
};
export var operations = Map({
  startsWith: function startsWith(field, value) {
    return "".concat(field, " =* \"").concat(value, "\"");
  },
  equals: function equals(field, value) {
    return "".concat(field, " = \"").concat(value, "\"");
  },
  lt: function lt(field, value) {
    return "".concat(field, " < \"").concat(value, "\"");
  },
  lteq: function lteq(field, value) {
    return "".concat(field, " <= \"").concat(value, "\"");
  },
  gt: function gt(field, value) {
    return "".concat(field, " > \"").concat(value, "\"");
  },
  gteq: function gteq(field, value) {
    return "".concat(field, " >= \"").concat(value, "\"");
  },
  "in": function _in(field, value) {
    return "".concat(field, " IN (").concat(value.map(function (v) {
      return "\"".concat(v, "\"");
    }).join(', '), ")");
  },
  between: function between(field, value) {
    return "".concat(field, " BETWEEN (\"").concat(value.get(0), "\", \"").concat(value.get(1), "\")");
  }
});

var searchFilters = function searchFilters(filters) {
  var q = Map(filters).filter(function (filter) {
    return filter.getIn(['value'], '') !== '';
  }).map(function (filter, key) {
    var mode = filter.getIn(['column', 'filter']);
    var op = operations.get(mode, operations.get('startsWith'));
    return op(key, filter.get('value'));
  }).toIndexedSeq().toList().join(' AND ');
  return q.length > 0 ? {
    q: q
  } : {};
};

export var generateSortParams = function generateSortParams(_ref7) {
  var sortColumn = _ref7.sortColumn,
      sortDirection = _ref7.sortDirection;
  return sortColumn ? {
    orderBy: sortColumn,
    direction: sortDirection
  } : {};
};
export var generatePaginationParams = function generatePaginationParams(_ref8) {
  var pageSize = _ref8.pageSize,
      nextPageToken = _ref8.nextPageToken;
  return pageSize && nextPageToken ? {
    limit: pageSize,
    pageToken: nextPageToken
  } : pageSize ? {
    limit: pageSize
  } : {};
};

var sortParams = function sortParams(sortColumn, sortDirection) {
  return sortColumn ? {
    orderBy: sortColumn,
    direction: sortDirection
  } : {};
};

export var generateCESearchParams = function generateCESearchParams(_ref9) {
  var pageSize = _ref9.pageSize,
      filters = _ref9.filters,
      sortColumn = _ref9.sortColumn,
      sortDirection = _ref9.sortDirection,
      nextPageToken = _ref9.nextPageToken;
  return _objectSpread(_objectSpread({
    limit: pageSize,
    pageToken: nextPageToken
  }, searchFilters(filters)), sortParams(sortColumn, sortDirection));
};